import { readProductTaxonomyFields } from './readProductTaxonomyFields'
import { uniqueSortedIds } from './productTaxonomyFields'

/**
 * UX mapping:
 * Основна категорія = canonicalCatalogTaxonomyNodeId (must be in assignments)
 * Додаткові = assignmentIds without main
 */
export function splitMainAndExtraAssignments(product = {}) {
  const taxonomy = readProductTaxonomyFields(product)
  const assignmentIds = taxonomy.catalogTaxonomyAssignmentIds
  const canonical = taxonomy.canonicalCatalogTaxonomyNodeId
  const mainId =
    typeof canonical === 'string' && assignmentIds.includes(canonical) ? canonical : null
  const extraIds = assignmentIds.filter((id) => id !== mainId)

  const hasCanonicalValue = typeof canonical === 'string' && canonical.length > 0
  const mainInvalid =
    hasCanonicalValue && !assignmentIds.includes(canonical)

  return {
    assignmentIds,
    mainId,
    extraIds,
    hasAssignments: assignmentIds.length > 0,
    hasMain: mainId != null,
    /** assignments exist but no valid main (missing or orphan canonical) */
    needsMainRecovery: assignmentIds.length > 0 && mainId == null,
    /** canonical set but not present in assignmentIds */
    mainInvalid,
  }
}

/** Set / change main: new leaf becomes canonical. Previous main is removed (not kept as extra). */
export function applySetMainCategory({
  assignmentIds = [],
  previousCanonical = null,
  mainId,
} = {}) {
  if (!mainId || typeof mainId !== 'string') {
    return {
      ok: false,
      code: 'missing_main',
      message: 'Оберіть основну категорію',
    }
  }

  const withoutPreviousMain =
    typeof previousCanonical === 'string' &&
    previousCanonical.length > 0 &&
    previousCanonical !== mainId
      ? assignmentIds.filter((id) => id !== previousCanonical)
      : assignmentIds

  const nextAssignments = uniqueSortedIds([...withoutPreviousMain, mainId])
  return {
    ok: true,
    catalogTaxonomyAssignmentIds: nextAssignments,
    canonicalCatalogTaxonomyNodeId: mainId,
    previousCanonical:
      typeof previousCanonical === 'string' && previousCanonical.length > 0
        ? previousCanonical
        : null,
  }
}

/** Add extra: never changes canonical. Requires existing main (caller enforces). */
export function applyAddExtraCategory({
  assignmentIds = [],
  previousCanonical = null,
  extraId,
} = {}) {
  if (!extraId || typeof extraId !== 'string') {
    return { ok: false, code: 'missing_extra', message: 'Оберіть додаткову категорію' }
  }
  if (!previousCanonical) {
    return {
      ok: false,
      code: 'main_required',
      message: 'Спочатку оберіть основну категорію товару',
    }
  }

  const nextAssignments = uniqueSortedIds([...assignmentIds, extraId])
  return {
    ok: true,
    catalogTaxonomyAssignmentIds: nextAssignments,
    canonicalCatalogTaxonomyNodeId: previousCanonical,
  }
}

/**
 * Remove category. Blocks removing main (canonical).
 */
export function applyRemoveCategoryAssignment({
  assignmentIds = [],
  previousCanonical = null,
  removeId,
} = {}) {
  if (!removeId) {
    return { ok: false, code: 'missing_id', message: 'Оберіть категорію' }
  }
  if (previousCanonical && removeId === previousCanonical) {
    return {
      ok: false,
      code: 'is_main',
      message: 'Категорія є основною. Спочатку змініть основну категорію.',
    }
  }

  const nextAssignments = uniqueSortedIds(assignmentIds.filter((id) => id !== removeId))
  let nextCanonical = previousCanonical
  if (nextCanonical && !nextAssignments.includes(nextCanonical)) {
    nextCanonical = nextAssignments.length === 1 ? nextAssignments[0] : null
  }

  return {
    ok: true,
    catalogTaxonomyAssignmentIds: nextAssignments,
    canonicalCatalogTaxonomyNodeId: nextCanonical,
  }
}

/** Bulk v1: products without valid main are eligible for primary categorization. */
export function partitionProductsForPrimaryCategorize(products = []) {
  const eligible = []
  const skipped = []
  products.forEach((product) => {
    const split = splitMainAndExtraAssignments(product)
    if (split.hasMain) {
      skipped.push({
        id: product.id,
        name: product.name,
        reason: 'Вже має основну категорію',
      })
      return
    }
    eligible.push(product)
  })
  return { eligible, skipped }
}
